/** @jsx jsx */

/* eslint-disable jsx-a11y/anchor-is-valid */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as ReactRouterLink } from 'react-router';

import classNames from 'classnames';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import useRouter from 'js/lib/useRouter';
import { humanizeLearningTime } from 'js/utils/DateTimeUtils';

// eslint-disable-next-line
import ItemMetadata from 'pages/open-course/common/models/itemMetadata';

import { Link, LoadingItem, Typography2 } from '@coursera/cds-core';
import { color } from '@coursera/coursera-ui';

import ItemIcon from 'bundles/item/components/ItemIcon';
import CrossBrowserFullscreenUtils from 'bundles/video-player/utils/CrossBrowserFullscreenUtils';

import _t from 'i18n!nls/video-player';

const styles = {
  autoNextOverlay: css`
    position: absolute;
    bottom: 0;
    height: 100%;
    width: 100%;
    background-color: rgba(0 0 0 / 80%);
    transition: background-color 0.5s linear;
  `,
  overlayDisplay: css`
    width: 50%;
  `,
  nextItemBtn: css`
    height: 96px;
    width: 96px;
    margin-right: 12px;
    color: #fff;
  `,
  itemInfo: css`
    font-size: 16px;
    color: #fff;

    > :not(:last-child) {
      margin-bottom: 12px;
    }

    .timer {
      font-weight: bold;
      font-size: 16px;
      color: #3cb371;
    }

    .timer-red {
      color: #ee4b2b;
    }
  `,
  nextItemName: css`
    font-size: 24px;
    font-weight: bold;
    text-decoration: none;
  `,
  link: css`
    color: var(--cds-color-neutral-background-primary) !important;
    text-decoration: none;

    &:hover {
      text-decoration: none;
    }
  `,
  nextItemDuration: css`
    color: #bdbdbd;
  `,
  nextItemCtaBtn: css`
    font-size: 16px;
    font-weight: 600;
    margin-top: 14px;
    text-decoration: none;
    text-transform: uppercase;
    color: var(--cds-color-neutral-background-primary);
  `,
  cancelBtn: css`
    margin-left: 1rem;
    color: var(--cds-color-neutral-background-primary);
  `,
};

type PropsFromCaller = {
  nextItem: ItemMetadata | undefined;
  isAutoNextEnabled: boolean;
  isAutoNextPaused?: boolean;
  defaultCountdown?: number;
};

const AutoNextOverlay = ({ defaultCountdown = 10, isAutoNextEnabled, isAutoNextPaused, nextItem }: PropsFromCaller) => {
  const router = useRouter();

  const [countdownTime, setCountdownTime] = useState(defaultCountdown);
  const [showTimer, setShowTimer] = useState(false);
  const [canceled, setCanceled] = useState(false);
  const [hasNextTransitionStarted, setHasNextTransitionStarted] = useState(false);

  const intervalId = useRef<number | null>(null);
  const nextItemLink = nextItem?.getLink();
  const type = nextItem?.getContent()?.typeName;

  const stopTimer = () => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
    }

    setShowTimer(false);
  };

  const handleNextTransition = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (event?: React.MouseEvent<typeof ReactRouterLink>) => {
      event?.stopPropagation();

      stopTimer();
      setHasNextTransitionStarted(true);
      if (CrossBrowserFullscreenUtils.isFullScreen()) {
        CrossBrowserFullscreenUtils.requestFullScreenExit();
      }
    },
    []
  );

  const cancelTransition = (event?: React.MouseEvent<HTMLElement>) => {
    if (event) {
      event.stopPropagation();
    }

    stopTimer();
    setCanceled(true);
  };

  const countdown = useCallback<() => $TSFixMe>(() => {
    // Since we are dependent on the countdown time from before, we should use the functional setState
    // so we immediately get the updated value.
    setCountdownTime((prevCountdownTime) => {
      if (prevCountdownTime === 1) {
        handleNextTransition();
        if (nextItemLink) {
          router.push(nextItemLink);
        }
        return prevCountdownTime;
      } else {
        return prevCountdownTime - 1;
      }
    });
  }, [handleNextTransition, nextItemLink, router]);

  useEffect(() => {
    if (isAutoNextEnabled) {
      intervalId.current = window.setInterval(countdown, 1000);
      setShowTimer(true);
    }

    return () => {
      stopTimer();
    };
  }, [countdown, isAutoNextEnabled]);

  if (!nextItem) {
    return null;
  }

  const name = nextItem.getName();
  const timeCommitment = nextItem.getTimeCommitment();
  const displayDuration = humanizeLearningTime(timeCommitment);

  return (
    // This onclick does not need to be exposed to screen readers, it exists to disable default behavior in a parent.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      css={styles.autoNextOverlay}
      data-testid="autoNextOverlay"
      className={classNames('rc-AutoNextOverlay', 'horizontal-box', 'align-items-absolute-center')}
      onClick={(ev) => ev.stopPropagation()}
    >
      <div css={styles.overlayDisplay} className="overlay-display horizontal-box">
        <div css={styles.nextItemBtn} className="next-item-button vertical-box">
          <Link
            data-testid={`next-item-icon-link-${type}`}
            component={ReactRouterLink}
            onClick={handleNextTransition}
            href="#"
            to={nextItemLink}
          >
            {/* TODO: In eventing v3, remember to track this as video_next_item_overlay.
             * We removed the eventing v3 events for this because the namespace app name set in VideoPlayer.tsx correctly formatted and it is too expensive
             * to change that since it would require DS/DE to change every usage of open_course.video events as well */}
            <ItemIcon
              size={96}
              type={type}
              style={{
                fill: color.white,
              }}
            />
          </Link>
        </div>
        <div className="next-item-display vertical-box">
          <div css={styles.itemInfo} className="item-info vertical-box">
            <span className="up-next">{_t('Up Next')}</span>
            <Link
              component={ReactRouterLink}
              /* TODO: In eventing v3, remember to track this as video_next_item_overlay
               * We removed the eventing v3 events for this because the namespace app name set in VideoPlayer.tsx correctly formatted and it is too expensive
               * to change that since it would require DS/DE to change every usage of open_course.video events as well */
              href="#"
              to={nextItemLink}
              onClick={handleNextTransition}
              css={[styles.nextItemName, styles.link]}
            >
              {name}
            </Link>
            <span css={styles.nextItemDuration} className="next-item-duration">
              {displayDuration}
            </span>
            {showTimer && !canceled && !hasNextTransitionStarted && (
              <FormattedHTMLMessage
                message={_t(
                  `Starts in <span class="timer">{countdownTime}</span> {countdownTime, plural, one {second} other {seconds}}`
                )}
                countdownTime={countdownTime}
              />
            )}
            {!showTimer && !canceled && !hasNextTransitionStarted && (
              <Typography2 className="timer-red" component="span" variant="subtitleMedium">
                {isAutoNextPaused ? _t('Autoplay paused') : _t('Autoplay disabled')}
              </Typography2>
            )}
            {hasNextTransitionStarted && <LoadingItem inverted label={_t('Almost ready...')} />}
          </div>

          <div className="horizontal-box align-items-vertical-center" style={{ marginLeft: '-33' }}>
            {nextItem && !hasNextTransitionStarted && (
              <Link
                /* TODO: In eventing v3, remember to track this as video_next_item_overlay
                 * We removed the eventing v3 events for this because the namespace app name set in VideoPlayer.tsx correctly formatted and it is too expensive
                 * to change that since it would require DS/DE to change every usage of open_course.video events as well */
                component={ReactRouterLink}
                href="#"
                to={nextItemLink}
                variant="quiet"
                onClick={handleNextTransition}
                className="next-item-cta-button"
                id="start-button"
                css={[styles.nextItemCtaBtn, styles.link]}
              >
                {_t('Start')}
              </Link>
            )}

            {nextItem && isAutoNextEnabled && showTimer && (
              <Link
                /* TODO: In eventing v3, remember to track this as video_next_item_overlay_cancel
                 * We removed the eventing v3 events for this because the namespace app name set in VideoPlayer.tsx correctly formatted and it is too expensive
                 * to change that since it would require DS/DE to change every usage of open_course.video events as well */
                href="#"
                onClick={cancelTransition}
                className="next-item-cta-button"
                id="cancel-button"
                css={[styles.nextItemCtaBtn, styles.cancelBtn, styles.link]}
              >
                {_t('Cancel')}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoNextOverlay;
